import { eventChannel } from 'redux-saga';
import { call, cancelled, take } from 'redux-saga/effects';

import {
  createActionReceivedService,
  createAttachmentReceivedService,
  createSiteReceivedService,
  createDeviceLabelReceivedService,
  createDeviceMembershipReceivedService,
  createDeviceReceivedService,
  createLabelReceivedService,
  createLineReceivedService,
  createNotificationReceivedService,
  createFactoryMembershipReceivedService,
  createFactoryReceivedService,
  createAlarmReceivedService,
  createUserReceivedService,
  createModelReceivedService,
  createProtocolReceivedService,
  createAlertSettingReceivedService,
  deleteActionReceivedService,
  deleteAttachmentReceivedService,
  deleteDeviceLabelReceivedService,
  deleteDeviceMembershipReceivedService,
  deleteDeviceReceivedService,
  deleteSiteReceivedService,
  deleteLabelReceivedService,
  deleteLineReceivedService,
  deleteNotificationReceivedService,
  deleteFactoryMembershipReceivedService,
  deleteFactoryReceivedService,
  deleteAlarmReceivedService,
  deleteUserReceivedService,
  deleteModelReceivedService,
  deleteProtocolReceivedService,
  deleteAlertSettingReceivedService,
  socketDisconnectedService,
  socketReconnectedService,
  updateActionReceivedService,
  updateAttachmentReceivedService,
  updateSiteReceivedService,
  updateDeviceReceivedService,
  updateLabelReceivedService,
  updateLineReceivedService,
  updateFactoryReceivedService,
  updateAlarmReceivedService,
  updateUserReceivedService,
  updateModelReceivedService,
  updateProtocolReceivedService,
  updateAlertSettingReceivedService,
  updateAlertsReceivedService,
  updateItemsDeviceReceivedService,
  hideDeviceReceivedService,
  updateComponentReceivedService,
  deleteComponentReceivedService,
  updateDiagramItemReceivedService,
  deleteDiagramItemReceivedService,
  createChannelReceivedService,
  updateChannelReceivedService,
  deleteChannelReceivedService,
  createItemReceivedService,
  updateItemReceivedService,
  deleteItemReceivedService,
} from '../services';
import api, { socket } from '../../../api';

const createSocketEventsChannel = () =>
  eventChannel((emit) => {
    const handleDisconnect = () => {
      emit([socketDisconnectedService]);
    };

    const handleReconnect = () => {
      emit([socketReconnectedService]);
    };

    const handleUserCreate = ({ item }) => {
      emit([createUserReceivedService, item]);
    };

    const handleUserUpdate = ({ item }) => {
      emit([updateUserReceivedService, item]);
    };

    const handleUserDelete = ({ item }) => {
      emit([deleteUserReceivedService, item]);
    };

    const handleFactoryCreate = ({ factory, factoryMembership }) => {
      emit([createFactoryReceivedService, factory, factoryMembership]);
    };

    const handleFactoryUpdate = ({ item }) => {
      emit([updateFactoryReceivedService, item]);
    };

    const handleFactoryDelete = ({ item }) => {
      emit([deleteFactoryReceivedService, item]);
    };

    const handleModelCreate = ({ item }) => {
      emit([createModelReceivedService, item]);
    };

    const handleModelUpdate = ({ item }) => {
      emit([updateModelReceivedService, item]);
    };

    const handleModelDelete = ({ item }) => {
      emit([deleteModelReceivedService, item]);
    };

    const handleProtocolCreate = ({ item }) => {
      emit([createProtocolReceivedService, item]);
    };

    const handleProtocolUpdate = ({ item }) => {
      emit([updateProtocolReceivedService, item]);
    };

    const handleProtocolDelete = ({ item }) => {
      emit([deleteProtocolReceivedService, item]);
    };

    const handleChannelCreate = ({ item }) => {
      emit([createChannelReceivedService, item]);
    };

    const handleChannelUpdate = ({ item }) => {
      emit([updateChannelReceivedService, item]);
    };

    const handleChannelDelete = ({ item }) => {
      emit([deleteChannelReceivedService, item]);
    };

    const handleItemCreate = ({ item }) => {
      emit([createItemReceivedService, item]);
    };

    const handleItemUpdate = ({ item }) => {
      emit([updateItemReceivedService, item]);
    };

    const handleItemDelete = ({ item }) => {
      emit([deleteItemReceivedService, item]);
    };

    const handleFactoryMembershipCreate = ({ item, included: { users } }) => {
      emit([createFactoryMembershipReceivedService, item, users[0]]);
    };

    const handleFactoryMembershipDelete = ({ item }) => {
      emit([deleteFactoryMembershipReceivedService, item]);
    };

    const handleSiteCreate = ({ item, included: { lines, labels } }) => {
      emit([createSiteReceivedService, item, lines, labels]);
    };

    const handleSiteUpdate = ({ item }) => {
      emit([updateSiteReceivedService, item]);
    };

    const handleSiteDelete = ({ item }) => {
      emit([deleteSiteReceivedService, item]);
    };

    const handleLineCreate = ({ item }) => {
      emit([createLineReceivedService, item]);
    };

    const handleLineUpdate = ({ item }) => {
      emit([updateLineReceivedService, item]);
    };

    const handleLineDelete = ({ item }) => {
      emit([deleteLineReceivedService, item]);
    };

    const handleLabelCreate = ({ item }) => {
      emit([createLabelReceivedService, item]);
    };

    const handleLabelUpdate = ({ item }) => {
      emit([updateLabelReceivedService, item]);
    };

    const handleLabelDelete = ({ item }) => {
      emit([deleteLabelReceivedService, item]);
    };

    const handleDeviceCreate = api.makeHandleDeviceCreate(
      ({ item, included: { deviceMemberships, deviceLabels, alarms, attachments } }) => {
        emit([
          createDeviceReceivedService,
          item,
          deviceMemberships,
          deviceLabels,
          alarms,
          attachments,
        ]);
      },
    );

    const handleDeviceUpdate = api.makeHandleDeviceUpdate(({ item }) => {
      emit([updateDeviceReceivedService, item]);
    });

    const handleDeviceHide = api.makeHandleDeviceHide(({ item }) => {
      emit([hideDeviceReceivedService, item]);
    });

    const handleDeviceDelete = api.makeHandleDeviceDelete(({ item }) => {
      emit([deleteDeviceReceivedService, item]);
    });

    const handleDeviceMembershipCreate = ({ item }) => {
      emit([createDeviceMembershipReceivedService, item]);
    };

    const handleDeviceMembershipDelete = ({ item }) => {
      emit([deleteDeviceMembershipReceivedService, item]);
    };

    const handleDeviceLabelCreate = ({ item }) => {
      emit([createDeviceLabelReceivedService, item]);
    };

    const handleDeviceLabelDelete = ({ item }) => {
      emit([deleteDeviceLabelReceivedService, item]);
    };

    const handleAlarmCreate = ({ item }) => {
      emit([createAlarmReceivedService, item]);
    };

    const handleAlarmUpdate = ({ items }) => {
      emit([updateAlarmReceivedService, items]);
    };

    const handleAlarmDelete = ({ item }) => {
      emit([deleteAlarmReceivedService, item]);
    };

    const handleAttachmentCreate = api.makeHandleAttachmentCreate(({ item, requestId }) => {
      emit([createAttachmentReceivedService, item, requestId]);
    });

    const handleAttachmentUpdate = api.makeHandleAttachmentUpdate(({ item }) => {
      emit([updateAttachmentReceivedService, item]);
    });

    const handleAttachmentDelete = api.makeHandleAttachmentDelete(({ item }) => {
      emit([deleteAttachmentReceivedService, item]);
    });

    const handleActionCreate = api.makeHandleActionCreate(({ item }) => {
      emit([createActionReceivedService, item]);
    });

    const handleActionUpdate = api.makeHandleActionUpdate(({ item }) => {
      emit([updateActionReceivedService, item]);
    });

    const handleActionDelete = api.makeHandleActionDelete(({ item }) => {
      emit([deleteActionReceivedService, item]);
    });

    const handleNotificationCreate = api.makeHandleNotificationCreate(
      ({ item, included: { users, devices, actions } }) => {
        emit([createNotificationReceivedService, item, users[0], devices[0], actions[0]]);
      },
    );

    const handleNotificationDelete = ({ item }) => {
      emit([deleteNotificationReceivedService, item]);
    };

    const handleAlertSettingCreate = ({ item }) => {
      emit([createAlertSettingReceivedService, item]);
    };

    const handleAlertSettingUpdate = ({ item }) => {
      emit([updateAlertSettingReceivedService, item]);
    };

    const handleAlertSettingDelete = ({ item }) => {
      emit([deleteAlertSettingReceivedService, item]);
    };

    const handleAlertUpdate = ({ items }) => {
      emit([updateAlertsReceivedService, items]);
    };

    const handleItemsDeivceUpdate = ({ items }) => {
      emit([updateItemsDeviceReceivedService, items]);
    };

    const handleDashboardUpdate = ({ item }) => {
      emit([updateComponentReceivedService, item]);
    };

    const handleDashboardDelete = ({ item }) => {
      emit([deleteComponentReceivedService, item?.id]);
    };

    const handleDiagramUpdate = ({ item }) => {
      emit([updateDiagramItemReceivedService, item]);
    };

    const handleDiagramDelete = ({ item }) => {
      emit([deleteDiagramItemReceivedService, item]);
    };

    socket.on('disconnect', handleDisconnect);
    socket.on('reconnect', handleReconnect);

    socket.on('userCreate', handleUserCreate);
    socket.on('userUpdate', handleUserUpdate);
    socket.on('userDelete', handleUserDelete);

    socket.on('factoryCreate', handleFactoryCreate);
    socket.on('factoryUpdate', handleFactoryUpdate);
    socket.on('factoryDelete', handleFactoryDelete);

    socket.on('modelCreate', handleModelCreate);
    socket.on('modelUpdate', handleModelUpdate);
    socket.on('modelDelete', handleModelDelete);

    socket.on('protocolCreate', handleProtocolCreate);
    socket.on('protocolUpdate', handleProtocolUpdate);
    socket.on('protocolDelete', handleProtocolDelete);

    socket.on('alertSettingCreate', handleAlertSettingCreate);
    socket.on('alertSettingUpdate', handleAlertSettingUpdate);
    socket.on('alertSettingDelete', handleAlertSettingDelete);

    socket.on('factoryMembershipCreate', handleFactoryMembershipCreate);
    socket.on('factoryMembershipDelete', handleFactoryMembershipDelete);

    socket.on('siteCreate', handleSiteCreate);
    socket.on('siteUpdate', handleSiteUpdate);
    socket.on('siteDelete', handleSiteDelete);

    socket.on('lineCreate', handleLineCreate);
    socket.on('lineUpdate', handleLineUpdate);
    socket.on('lineDelete', handleLineDelete);

    socket.on('labelCreate', handleLabelCreate);
    socket.on('labelUpdate', handleLabelUpdate);
    socket.on('labelDelete', handleLabelDelete);

    socket.on('deviceCreate', handleDeviceCreate);
    socket.on('deviceUpdate', handleDeviceUpdate);
    socket.on('deviceHide', handleDeviceHide);
    socket.on('deviceDelete', handleDeviceDelete);

    socket.on('deviceMembershipCreate', handleDeviceMembershipCreate);
    socket.on('deviceMembershipDelete', handleDeviceMembershipDelete);

    socket.on('deviceLabelCreate', handleDeviceLabelCreate);
    socket.on('deviceLabelDelete', handleDeviceLabelDelete);

    socket.on('alarmCreate', handleAlarmCreate);
    socket.on('alarmUpdate', handleAlarmUpdate);
    socket.on('alarmDelete', handleAlarmDelete);

    socket.on('attachmentCreate', handleAttachmentCreate);
    socket.on('attachmentUpdate', handleAttachmentUpdate);
    socket.on('attachmentDelete', handleAttachmentDelete);

    socket.on('actionCreate', handleActionCreate);
    socket.on('actionUpdate', handleActionUpdate);
    socket.on('actionDelete', handleActionDelete);

    socket.on('notificationCreate', handleNotificationCreate);
    socket.on('notificationUpdate', handleNotificationDelete);

    socket.on('alertUpdate', handleAlertUpdate);
    socket.on('itemUpdate', handleItemsDeivceUpdate);

    socket.on('dashboardUpdate', handleDashboardUpdate);
    socket.on('dashboardDelete', handleDashboardDelete);

    socket.on('skeletonUpdate', handleDiagramUpdate);
    socket.on('skeletonDelete', handleDiagramDelete);

    socket.on('channelCreate', handleChannelCreate);
    socket.on('channelUpdate', handleChannelUpdate);
    socket.on('channelDelete', handleChannelDelete);

    socket.on('deviceItemCreate', handleItemCreate);
    socket.on('deviceItemUpdate', handleItemUpdate);
    socket.on('deviceItemDelete', handleItemDelete);

    return () => {
      socket.off('disconnect', handleDisconnect);
      socket.off('reconnect', handleReconnect);

      socket.off('userCreate', handleUserCreate);
      socket.off('userUpdate', handleUserUpdate);
      socket.off('userDelete', handleUserDelete);

      socket.off('factoryCreate', handleFactoryCreate);
      socket.off('factoryUpdate', handleFactoryUpdate);
      socket.off('factoryDelete', handleFactoryDelete);

      socket.off('modelCreate', handleModelCreate);
      socket.off('modelUpdate', handleModelUpdate);
      socket.off('modelDelete', handleModelDelete);

      socket.off('protocolCreate', handleProtocolCreate);
      socket.off('protocolUpdate', handleProtocolUpdate);
      socket.off('protocolDelete', handleProtocolDelete);

      socket.off('alertSettingCreate', handleAlertSettingCreate);
      socket.off('alertSettingUpdate', handleAlertSettingUpdate);
      socket.off('alertSettingDelete', handleAlertSettingDelete);

      socket.off('factoryMembershipCreate', handleFactoryMembershipCreate);
      socket.off('factoryMembershipDelete', handleFactoryMembershipDelete);

      socket.off('siteCreate', handleSiteCreate);
      socket.off('siteUpdate', handleSiteUpdate);
      socket.off('siteDelete', handleSiteDelete);

      socket.off('lineCreate', handleLineCreate);
      socket.off('lineUpdate', handleLineUpdate);
      socket.off('lineDelete', handleLineDelete);

      socket.off('labelCreate', handleLabelCreate);
      socket.off('labelUpdate', handleLabelUpdate);
      socket.off('labelDelete', handleLabelDelete);

      socket.off('deviceCreate', handleDeviceCreate);
      socket.off('deviceUpdate', handleDeviceUpdate);
      socket.off('deviceHide', handleDeviceHide);
      socket.off('deviceDelete', handleDeviceDelete);

      socket.off('deviceMembershipCreate', handleDeviceMembershipCreate);
      socket.off('deviceMembershipDelete', handleDeviceMembershipDelete);

      socket.off('deviceLabelCreate', handleDeviceLabelCreate);
      socket.off('deviceLabelDelete', handleDeviceLabelDelete);

      socket.off('alarmCreate', handleAlarmCreate);
      socket.off('alarmUpdate', handleAlarmUpdate);
      socket.off('alarmDelete', handleAlarmDelete);

      socket.off('attachmentCreate', handleAttachmentCreate);
      socket.off('attachmentUpdate', handleAttachmentUpdate);
      socket.off('attachmentDelete', handleAttachmentDelete);

      socket.off('actionCreate', handleActionCreate);
      socket.off('actionUpdate', handleActionUpdate);
      socket.off('actionDelete', handleActionDelete);

      socket.off('notificationCreate', handleNotificationCreate);
      socket.off('notificationUpdate', handleNotificationDelete);

      socket.off('alertUpdate', handleAlertUpdate);

      socket.off('itemUpdate', updateItemsDeviceReceivedService);

      socket.off('dashboardUpdate', handleDashboardUpdate);
      socket.off('dashboardDelete', handleDashboardDelete);

      socket.off('skeletonUpdate', handleDiagramUpdate);
      socket.off('skeletonDelete', handleDiagramDelete);

      socket.off('channelCreate', handleChannelCreate);
      socket.off('channelUpdate', handleChannelUpdate);
      socket.off('channelDelete', handleChannelDelete);

      socket.off('deviceItemCreate', handleItemCreate);
      socket.off('deviceItemUpdate', handleItemUpdate);
      socket.off('deviceItemDelete', handleItemDelete);
    };
  });

export default function* socketWatchers() {
  const socketEventsChannel = yield call(createSocketEventsChannel);

  try {
    while (true) {
      const args = yield take(socketEventsChannel);

      yield call(...args);
    }
  } finally {
    if (yield cancelled()) {
      socketEventsChannel.close();
    }
  }
}
